import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
	ProductApi,
	Category,
	Languages,
	Shop,
	OrderProduct,
	OrderObject,
	Location,
	ById,
} from 'common/types';
import { ReduxState, LoadingData } from 'services/types';
import {
	Theme,
	Dialog,
	Toolbar,
	IconButton,
	Button,
	Grid,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import CloseIcon from '@mui/icons-material/Close';
import classNames from 'classnames';
import Container from 'components/Container';
import Badge from '@mui/material/Badge';
import Spinner from 'components/Spinner';
import UserMenu from 'components/UserMenu';
import ProductSelection from 'components/ProductSelection';
import Paper from '@mui/material/Paper';
import ShoppingCartForUpdating from './ShoppingCartForUpdating';
import ShoppingCartDialogUpdating from './ShoppingCartDialogUpdating';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import moment from 'moment-timezone';
import Drawer from '@mui/material/Drawer';
import {
	getOverbookedVariantIds,
	getProductCurrentAvailabilityCount,
	getSelectedVariantsCount,
	ProductTotalAvailability,
} from 'common/api/frontend/inventory_new';
import { updateActiveShopperId } from 'actions';
import { updateActiveRentalToDb } from 'services/utils';
import { createUpdatedRentalOrderObject, getDurationFromProducts } from './utils';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { getPricingString } from 'common/utils/common';
import {
	getEarliestProductStartTime,
	getLatestProductEndTime,
} from 'common/utils/dateCalculations';
import { getShopLocationsWithContact } from 'common/utils/shopUtils';
import {
	getLocationStockProducts,
	getStockProductsWithSetProducts,
} from 'common/utils/productVisibilityFilters';
import { useTranslation } from 'services/localization/useTranslation';
import {
	getEndDateWithInventoryBlockers,
	getStartDateWithInventoryBlockers,
} from 'common/utils/productUtils';
import { getOrderPricing, getPricingOptions } from 'common/modules/orders';
import { usePermissions } from 'hooks/usePermissions';
import RentalSearchBar from 'views/RentalsView/RentalSearchBar/RentalSearchBar';
import { handleQuantityAndAvailabilityFetch } from 'common/api/frontend/inventory_new/getQuantitiesAndAvailabilities';
import { useHasFeature } from 'hooks';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';

interface DispatchProps {
	updateActiveShopperId: (shopperId: string) => void;
}

interface OwnProps {
	open: boolean;
	activeRental: OrderObject;
	activeShopperId: string | null;
	handleClose: () => void;
	newPerson?: {
		handleContinue: (productsToAdd: OrderProduct[]) => void;
		productsToAdd: OrderProduct[];
	};
}

interface StateToProps {
	categories: LoadingData<Category[]>;
	stockProducts: LoadingData<ProductApi[]>;
	activeStoreRental: LoadingData<OrderObject> | null;
	activeShop: Shop;
	lang: Languages;
}

type Props = OwnProps & StateToProps & DispatchProps & WithStyles<typeof styles>;

export const AddProduct = (props: Props) => {
	const {
		classes,
		open,
		handleClose,
		stockProducts,
		categories,
		lang,
		activeShop,
		activeRental,
		updateActiveShopperId,
		newPerson,
	} = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const { hasFeature } = useHasFeature();
	const { products: rentalProducts, shoppers, orderDelivery } = activeRental;
	const activeShopperId: string = props.activeShopperId || shoppers[0].id;
	const shopId = activeShop.shopInfo.id;
	const [productAvailabilitiesLocalState, setProductAvailabilitiesLocalState] = useState<
		ById<ProductTotalAvailability>
	>({});
	const [selectedCategoryId, setSelectedCategoryId] = useState('all');
	const [loadingProductAvailability, setLoadingProductAvailability] = useState<boolean>(true);
	const [productsToAdd, setProductsToAdd] = useState<OrderProduct[]>(
		newPerson ? newPerson.productsToAdd : [],
	);
	const [updatingOrder, setUpdatingOrder] = useState(false);
	const [shoppingCartOpen, setShoppingCartOpen] = useState(false);
	const [
		availabilityNotLoadedForLongDuration,
		setAvailabilityNotLoadedForLongDuration,
	] = useState<boolean>(false);

	const [searchInput, setSearchInput] = useState<string>('');
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const prevAddedProducts = usePrevious(productsToAdd);
	const activeShopper = shoppers.find((s) => s.id === activeShopperId);
	// Take all rental products to calculate start and end day for new person
	const activeShoppersProducts = !newPerson
		? rentalProducts.filter((p) => p.shopperId === activeShopperId)
		: rentalProducts;
	const activeShopperSegment = activeShopper ? activeShopper.segment : undefined;
	const start =
		activeRental.rentalInfo.rentalState === 'ACTIVE'
			? moment().toISOString()
			: getEarliestProductStartTime(activeShoppersProducts) || '';
	const end: string = getLatestProductEndTime(activeShoppersProducts) || '';
	const duration = getDurationFromProducts(activeShoppersProducts) ?? {
		durationName: null,
		durationType: '24h',
		durationInSeconds: moment(end).diff(moment(start), 'seconds'),
	};
	const shopLocations = getShopLocationsWithContact(activeShop.publicInfo);
	const startLocationId = activeRental.rentalInfo.startLocation.id;

	const allStockProductsData = stockProducts.data;
	const currency = getCurrencyObjectFromCurrencyCode(
		activeRental.rentalInfo.pricing.currency,
		activeShop.publicInfo.currency,
	);
	const locationStockProducts = allStockProductsData
		? getLocationStockProducts(allStockProductsData, startLocationId, 'ADMIN')
		: null;

	const locationStockProductsWithSetProducts =
		allStockProductsData && locationStockProducts
			? getStockProductsWithSetProducts(locationStockProducts, allStockProductsData)
			: [];

	const overbookedVariantIds = getOverbookedVariantIds(
		productAvailabilitiesLocalState,
		productsToAdd,
	);

	const { hasPermission } = usePermissions();

	const allowOverbookings = hasPermission('allow_overbookings');

	const newOrderProducts = [...rentalProducts, ...productsToAdd];
	const orderPricing = getOrderPricing(
		newOrderProducts,
		orderDelivery,
		getPricingOptions(activeRental.rentalInfo.pricing),
	);
	const newRental: OrderObject = {
		...activeRental,
		products: newOrderProducts,
		rentalInfo: {
			...activeRental.rentalInfo,
			pricing: orderPricing,
		},
	};

	useEffect(() => {
		const durationDiff = moment.duration(moment(end).diff(moment(start)));
		if (locationStockProductsWithSetProducts && allStockProductsData) {
			if (hasFeature('DISABLE_PRODUCT_AVAILABILITY_LOADING', { scope: 'shop' })) {
				/* TEMPORARY - remove when performance / long loading issue in the new rental
				view has been fixed */
				setAvailabilityNotLoadedForLongDuration(true);
				setLoadingProductAvailability(false);
			} else if (durationDiff.asDays() < 30) {
				setAvailabilityNotLoadedForLongDuration(false);
				const productsToFetchAvailabilityFor = locationStockProductsWithSetProducts;
				handleAvailabilityFetchForAll(
					productsToFetchAvailabilityFor,
					allStockProductsData,
					startLocationId,
					shopLocations,
				);
			} else {
				setAvailabilityNotLoadedForLongDuration(true);
				setLoadingProductAvailability(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stockProducts, open]);

	useEffect(() => {
		if (prevAddedProducts && allStockProductsData) {
			handleUpdateProductAvailability(prevAddedProducts, allStockProductsData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productsToAdd]);

	useEffect(() => {
		if (allStockProductsData && !loadingProductAvailability) {
			handleUpdateProductAvailability([], allStockProductsData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadingProductAvailability]);

	const handleUpdateProductAvailability = (
		prevAddedProducts: OrderProduct[],
		stockProducts: ProductApi[],
	) => {
		const updatedProductAvailabilities = { ...productAvailabilitiesLocalState };

		stockProducts
			.map((p) => p.id)
			.forEach((pId) => {
				const productAvailability = productAvailabilitiesLocalState[pId];
				if (!!productAvailability) {
					const product = stockProducts.find((p) => p.id === pId);
					if (!!product) {
						const updatedAvailability = getProductCurrentAvailabilityCount(
							product,
							productAvailability,
							stockProducts,
							getSelectedVariantsCount(productsToAdd),
						);
						updatedProductAvailabilities[pId] = {
							...productAvailabilitiesLocalState[pId],
							productAvailabilityWithOrderProducts: updatedAvailability,
						};
					}
				}
			});

		setProductAvailabilitiesLocalState(updatedProductAvailabilities);
	};

	const handleAvailabilityFetchForAll = async (
		products: ProductApi[],
		allStockProducts: ProductApi[],
		startLocationId: string,
		shopLocations: Location[],
	) => {
		setLoadingProductAvailability(true);
		const productAvailabilities = await Promise.all(
			products.map((product) => {
				const startDateWithInventoryBlockers = getStartDateWithInventoryBlockers({
					startDate: start,
				});
				const endDateWithInventoryBlockers = getEndDateWithInventoryBlockers(
					end,
					product,
					allStockProducts,
				);
				return handleQuantityAndAvailabilityFetch({
					product,
					stockProducts: allStockProducts,
					startDate: startDateWithInventoryBlockers,
					endDate: endDateWithInventoryBlockers,
					shopId,
					startLocationId,
					shopLocations,
					purchaseType: 'rental',
				});
			}),
		);

		const updatedProductAvailabilities = {
			...productAvailabilitiesLocalState,
		};
		productAvailabilities.forEach((productAvailabilityObject) => {
			const product = allStockProducts.find((p) => p.id === productAvailabilityObject.id);
			if (product) {
				const availabilityWithOrderProducts = getProductCurrentAvailabilityCount(
					product,
					productAvailabilityObject,
					allStockProducts,
					getSelectedVariantsCount(productsToAdd),
				);
				updatedProductAvailabilities[product.id] = productAvailabilityObject;
				updatedProductAvailabilities[
					product.id
				].productAvailabilityWithOrderProducts = availabilityWithOrderProducts;
			}
		});
		setProductAvailabilitiesLocalState(updatedProductAvailabilities);
		setLoadingProductAvailability(false);
	};

	const handleSelectedCategoryIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedCategoryId(e.target.value);
	};

	const handleProductAdd = (product: OrderProduct) => {
		setProductsToAdd((prevProducts) => {
			return [...prevProducts, product];
		});
	};

	const handleProductDelete = (pId: string) => {
		setProductsToAdd((prevProducts) => {
			const updatedProducts = prevProducts.filter((p) => p.id !== pId);
			return updatedProducts;
		});
	};

	const updateShopperAdditionalProducts = (newProducts: OrderProduct[], shopperId: string) => {
		setProductsToAdd(newProducts);
	};

	const handleUpdateOrder = async () => {
		if (newPerson) {
			newPerson.handleContinue(productsToAdd);
			return;
		}
		setUpdatingOrder(true);
		const updatedRentalOrderObject = createUpdatedRentalOrderObject(
			newRental.rentalInfo,
			rentalProducts,
			productsToAdd,
			shoppers,
			orderDelivery,
		);

		try {
			await updateActiveRentalToDb(updatedRentalOrderObject);
			setUpdatingOrder(false);
			setProductsToAdd([]);
			handleClose();
		} catch (e) {
			setUpdatingOrder(false);
			// TODO - feedback if did not work.
		}
	};

	const handleCloseDialog = () => {
		setProductsToAdd([]);
		handleClose();
	};

	const handleSearchInputChange = (e: string) => {
		setSearchInput(e);
	};

	const renderUpdateOrderBtn = () => {
		const productsCount = productsToAdd.length;
		return (
			<Button
				variant="contained"
				color="primary"
				disabled={
					productsCount === 0 ||
					updatingOrder ||
					(!!overbookedVariantIds.length && !allowOverbookings)
				}
				className={classNames(
					classes.fixedContinueBtn,
					classes.continueBtnMinHeight,
					!isMobile ? classes.continueBtnMargin : {},
				)}
				onClick={handleUpdateOrder}
				data-qa="continueButton"
			>
				{updatingOrder ? (
					<Spinner padding={8} thickness={4} />
				) : newPerson ? (
					t('common:actions.continue')
				) : (
					t('shopperProducts.updateOrder')
				)}
			</Button>
		);
	};

	const renderSideBar = () => {
		return (
			<Paper square elevation={2} className={classes.sideBarCart}>
				<ShoppingCartForUpdating
					activeRental={newRental}
					newProducts={productsToAdd}
					lang={lang}
					activeShopperId={activeShopperId}
					currency={currency}
					newPerson={newPerson}
					overbookedVariantIds={overbookedVariantIds}
				/>
				{renderUpdateOrderBtn()}
			</Paper>
		);
	};

	const renderBottomBar = () => {
		const productsCount = newOrderProducts.length;
		return (
			<Drawer
				open={productsCount > 0}
				variant="persistent"
				classes={{
					paper: classes.bottomMenu,
				}}
				anchor={'bottom'}
			>
				<Grid
					container
					justifyContent="flex-start"
					alignItems="center"
					className={classes.iconWrapper}
					onClick={() => setShoppingCartOpen(!shoppingCartOpen)}
				>
					<Badge className={classes.leftIcon} badgeContent={productsCount} color="primary">
						<ShoppingCartIcon color="inherit" />
					</Badge>
					<Typography variant="subtitle1" color="inherit">
						{getPricingString(orderPricing.total || 0, currency)}
					</Typography>
				</Grid>
				{renderUserMenu()}
				<Grid
					container
					justifyContent="flex-end"
					alignItems="center"
					className={classes.iconWrapper}
				>
					{renderUpdateOrderBtn()}
				</Grid>

				<ShoppingCartDialogUpdating
					open={shoppingCartOpen}
					onClose={() => setShoppingCartOpen(false)}
					activeRental={newRental}
					newProducts={productsToAdd}
					lang={lang}
					activeShopperId={activeShopperId}
					currency={currency}
					overbookedVariantIds={overbookedVariantIds}
				/>
			</Drawer>
		);
	};

	const renderUserMenu = () => {
		const activeShopperIndex = shoppers.reduce(
			(activeIndex: number, shopper, index) =>
				shopper.id === activeShopperId ? index : activeIndex,
			0,
		);
		return (
			<UserMenu
				shoppers={shoppers}
				activeShopperIndex={activeShopperIndex}
				activeShopperId={activeShopperId}
				changeActiveShopperId={(activeShopperId: string) => updateActiveShopperId(activeShopperId)}
			/>
		);
	};

	return (
		<Dialog fullScreen open={open} onClose={handleClose}>
			<Toolbar className={classes.toolBar}>
				<IconButton
					edge="start"
					color="inherit"
					onClick={() => handleCloseDialog()}
					aria-label="Close"
					size="large"
				>
					<CloseIcon />
				</IconButton>
				<Typography variant="h5">
					{newPerson
						? t('shopperProducts.addProductsForNewPerson', 'Add products for new person')
						: t('shopperProducts.addProducts')}
				</Typography>
			</Toolbar>
			<div className={classes.root}>
				<div className={classes.contentWrapper}>
					<div className={classes.productView}>
						<Container className={classes.main}>
							{locationStockProducts && categories.data && (
								<RentalSearchBar
									inputValue={searchInput}
									onInputChange={handleSearchInputChange}
									categories={categories.data}
									locationStockProducts={locationStockProducts}
									selectedCategoryId={selectedCategoryId}
									lang={lang}
									handleSelectedCategoryIdChange={handleSelectedCategoryIdChange}
								/>
							)}
							{locationStockProducts && categories.data ? (
								<ProductSelection
									stockProducts={locationStockProductsWithSetProducts}
									rentalProducts={productsToAdd}
									categories={categories.data}
									selectedCategoryId={selectedCategoryId}
									activeShopperId={activeShopperId}
									activeShopperSegment={activeShopperSegment}
									lang={lang}
									duration={duration}
									startDate={start}
									endDate={end}
									activeShop={activeShop}
									rentalInfo={newRental.rentalInfo}
									productAvailabilities={productAvailabilitiesLocalState}
									loadingProductAvailability={loadingProductAvailability}
									handleProductAdd={(p: OrderProduct) => handleProductAdd(p)}
									handleProductDelete={(pId: string) => handleProductDelete(pId)}
									updateShopperAdditionalProducts={(
										newProducts: OrderProduct[],
										shopperId: string,
									) => updateShopperAdditionalProducts(newProducts, shopperId)}
									availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
									locationId={startLocationId}
									searchInput={searchInput}
									handleClearSearch={setSearchInput}
								/>
							) : stockProducts.loading ? (
								<Spinner in={true} />
							) : (
								<div>{stockProducts.error}</div>
							)}
						</Container>
					</div>
					{!isMobile && renderSideBar()}
				</div>
				{isMobile && renderBottomBar()}
			</div>
		</Dialog>
	);
};

const mapStateToProps = (state: ReduxState): StateToProps => {
	return {
		categories: state.stock.categories,
		stockProducts: state.stock.stockProducts,
		activeStoreRental: RentalViewSelectors._activeOrder(state),
		activeShop: state.shop.shop.data!,
		lang: state.lang,
	};
};

const styles = (theme: Theme) =>
	createStyles({
		toolBar: {
			display: 'flex',
			justifyContent: 'flex-start',
			alignItems: 'center',
		},
		root: {
			display: 'flex',
			flexDirection: 'column',
			flex: '1 1 auto',
			overflow: 'hidden',
			height: 'calc(100vh - 56px)', // Height of header bar
			[theme.breakpoints.up('sm')]: {
				height: 'calc(100vh - 64px)', // Height of header bar
			},
		},
		contentWrapper: {
			display: 'flex',
			flexDirection: 'row',
			flex: '1 1 auto',
			height: '100%',
		},
		productView: {
			flex: '1 1 auto',
			overflow: 'auto',
			'-webkit-overflow-scrolling': 'touch',
			display: 'flex',
			flexDirection: 'column',
			background: theme.palette.background.secondary,
		},
		continueBtnMargin: {
			margin: theme.spacing(2, 0),
		},
		main: {
			paddingTop: theme.spacing(2),

			paddingBottom: theme.spacing(10),
			overflow: 'auto',
			'-webkit-overflow-scrolling': 'touch',
		},
		sideBarCart: {
			flex: '1 0 25%',
			maxWidth: 300,
			minWidth: 250,
			backgroundColor: theme.palette.background.paper,
			zIndex: 2,
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
		},
		durationWrapper: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		duration: {
			fontSize: theme.spacing(1.75),
		},
		fixedContinueBtn: {
			flex: '0 0 auto',
			margin: theme.spacing(0, 2),
		},
		continueBtnMinHeight: {
			minHeight: 48,
		},
		bottomMenu: {
			height: 64,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			flexDirection: 'row',
			padding: '8px 16px',
			overflow: 'unset',
			backgroundColor: theme.palette.background.paper,
		},
		iconWrapper: {
			display: 'flex',
			alignItems: 'center',
			height: '100%',
			cursor: 'pointer',
			flex: '1',
		},
		leftIcon: {
			marginRight: 12,
		},
		rightIcon: {
			marginRight: -theme.spacing(1),
		},
	});

const dispatchProps: DispatchProps = {
	updateActiveShopperId,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(AddProduct));

const usePrevious = <T extends {}>(value: T) => {
	const ref = useRef<T>();

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
};
